/**
 * Quiet transform-mode toggle shared by the pattern editor and value-map editor
 * dialogs (column-mapping-ui spec, "Constant and value-map editors"). Fixes the
 * browser-QA regression where value-map mode had no UI affordance: the column
 * edit pencil always opened the pattern editor and nothing ever set a
 * `{type:'valueMap'}` transform. This toggle lets the operator pick which
 * transform type applies to a source column from inside either dialog — a
 * quiet inline control (no colored pills), per spec: "Transform affordances
 * SHALL be quiet inline controls, not colored pills."
 */
import { Button } from '@ooh/ui/components/button';
import { cn } from '@ooh/ui/lib/utils';

export type TransformMode = 'pattern' | 'valueMap';

export interface TransformModeToggleLabels {
  transformModePatternLabel: string;
  transformModeValueMapLabel: string;
  /** InfoHint trigger label for the mode toggle, e.g. "Czym różnią się tryby?" */
  modeToggleInfoLabel: string;
  /** InfoHint body explaining the difference between Pattern and Value Map mode. */
  modeToggleInfoText: string;
}

export interface TransformModeToggleProps {
  mode: TransformMode;
  labels: TransformModeToggleLabels;
  onModeChange: (mode: TransformMode) => void;
}

function modeButtonClassName(active: boolean): string {
  return cn(
    active ? 'text-foreground font-semibold' : 'text-muted-foreground underline underline-offset-2',
  );
}

export function TransformModeToggle({ mode, labels, onModeChange }: TransformModeToggleProps) {
  return (
    <div className="flex items-center gap-1.5 text-xs" data-testid="transform-mode-toggle">
      <Button
        type="button"
        variant="inline"
        className={modeButtonClassName(mode === 'pattern')}
        onClick={() => onModeChange('pattern')}
        aria-pressed={mode === 'pattern'}
        data-testid="transform-mode-pattern"
      >
        {labels.transformModePatternLabel}
      </Button>
      <span className="text-muted-foreground/50" aria-hidden="true">
        ·
      </span>
      <Button
        type="button"
        variant="inline"
        className={modeButtonClassName(mode === 'valueMap')}
        onClick={() => onModeChange('valueMap')}
        aria-pressed={mode === 'valueMap'}
        data-testid="transform-mode-value-map"
      >
        {labels.transformModeValueMapLabel}
      </Button>
    </div>
  );
}
